import { useEffect, useState } from "react";
import { Search } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { toast } from "sonner";
import { money, pct, monthLabel } from "../lib/format";
import { PageHeader, Panel, Empty, TrendChart } from "../components/Widgets";
import { Input } from "../components/ui/input";
import { LostMerchants } from "../components/Insights";
import { NotesPanel } from "../components/Notes";
import { TagChips, TagEditor } from "../components/Extras";
import { Checkbox } from "../components/ui/checkbox";
import { Button } from "../components/ui/button";
import { ReassignPanel } from "../components/Reassign";
import { MerchantFeePanel, PendingFees } from "../components/MerchantFees";
import { MerchantBrandPanel, PendingBrands } from "../components/Brands";
import { BulkReassignDialog } from "../components/Forecast";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { UnassignedMerchants } from "../components/UnassignedMerchants";

export default function Merchants() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [sel, setSel] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tags, setTags] = useState([]);
  const [tagMap, setTagMap] = useState({});
  const [tag, setTag] = useState(null);
  const [checked, setChecked] = useState([]);
  const [bulkTag, setBulkTag] = useState("");
  const [moveOpen, setMoveOpen] = useState(false);
  const afterMove = () => {
    setMoveOpen(false); setChecked([]);
    if (tag) pickTag(tag); else if (q.trim().length >= 2) api.get("/merchants/search", { params: { q } }).then((r) => setResults(r.data));
    if (sel) open(sel.merchant_id);
  };
  useEffect(() => { setChecked([]); }, [results]);
  const applyBulk = async () => {
    try {
      const { data } = await api.post("/tags/bulk", { merchant_ids: checked, tag: bulkTag });
      toast.success(`Tagged ${data.tagged} merchant${data.tagged === 1 ? "" : "s"} "${data.tag}"`);
      setBulkTag(""); setChecked([]); loadTags();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const loadTags = () => {
    api.get("/tags").then((r) => setTags(r.data));
    api.get("/tags/map").then((r) => setTagMap(r.data));
  };
  useEffect(() => { loadTags(); }, []);

  const pickTag = (t) => {
    if (tag === t) { setTag(null); setResults(null); return; }
    setTag(t); setQ("");
    api.get(`/tags/${encodeURIComponent(t)}/merchants`).then((r) => setResults(r.data)).catch((e) => toast.error(errMsg(e)));
  };

  useEffect(() => {
    if (q.trim().length < 2) { if (!tag) setResults(null); return; }
    setTag(null);
    let live = true;
    const t = setTimeout(() => {
      setBusy(true);
      api.get("/merchants/search", { params: { q } }).then((r) => live && setResults(r.data))
        .catch((e) => toast.error(errMsg(e))).finally(() => setBusy(false));
    }, 250);
    return () => { live = false; clearTimeout(t); };
  }, [q]); // eslint-disable-line react-hooks/exhaustive-deps

  const open = (mid) => api.get(`/merchants/${mid}`).then((r) => setSel(r.data)).catch((e) => toast.error(errMsg(e)));

  return (
    <div>
      <PageHeader eyebrow="Directory" title="Merchants" />
      <Tabs defaultValue="lookup">
        <TabsList className="mb-4">
          <TabsTrigger value="lookup" data-testid="merchants-tab-lookup">Lookup</TabsTrigger>
          <TabsTrigger value="untagged" data-testid="merchants-tab-untagged">Untagged</TabsTrigger>
          <TabsTrigger value="unassigned" data-testid="merchants-tab-unassigned">Unassigned</TabsTrigger>
        </TabsList>
        <TabsContent value="unassigned"><UnassignedMerchants /></TabsContent>
        <TabsContent value="untagged"><PendingBrands full /></TabsContent>
        <TabsContent value="lookup">
      <div className="grid lg:grid-cols-[380px_1fr] gap-6">
        <Panel>
          <div className="p-4 border-b border-slate-800 relative">
            <Search size={14} className="absolute left-7 top-7 text-slate-500" />
            <Input data-testid="merchant-search-input" autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Merchant number or name" className="pl-9 bg-[#0B0F17]" />
            {tags.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-3" data-testid="tag-filters">
                {tags.map((t) => (
                  <button key={t.tag} data-testid={`tag-filter-${t.tag}`} onClick={() => pickTag(t.tag)}
                    className={`text-[11px] font-mono px-2 py-0.5 rounded border transition-colors ${tag === t.tag ? "bg-blue-500/20 border-blue-500/50 text-blue-200" : "border-slate-700 text-slate-400 hover:text-blue-300"}`}>
                    {t.tag} <span className="text-slate-500">{t.count}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          {busy && !results ? <Empty>Searching…</Empty> : !results ? <Empty>Type at least 2 characters.</Empty> : results.length === 0 ? <Empty>No merchants found.</Empty> : (
            <div className="max-h-[560px] overflow-y-auto divide-y divide-slate-800" data-testid="merchant-results">
              <div className="flex items-center gap-2 px-4 py-2 bg-[#0d1320] sticky top-0 z-10" data-testid="bulk-tag-bar">
                <Checkbox data-testid="select-all-merchants" checked={checked.length === results.length && results.length > 0}
                  onCheckedChange={(v) => setChecked(v ? results.map((m) => m.merchant_id) : [])} />
                <span className="text-xs text-slate-400 flex-1">{checked.length ? `${checked.length} selected` : "Select merchants"}</span>
                {checked.length > 0 && <>
                  <Input data-testid="bulk-tag-input" value={bulkTag} onChange={(e) => setBulkTag(e.target.value)} placeholder="Tag" className="h-7 w-28 text-xs bg-[#0B0F17]" />
                  <Button data-testid="bulk-tag-button" size="sm" className="h-7 bg-blue-500 hover:bg-blue-600 text-white" disabled={!bulkTag.trim()} onClick={applyBulk}>Tag</Button>
                  <Button data-testid="bulk-move-button" size="sm" variant="outline" className="h-7" onClick={() => setMoveOpen(true)}>Move</Button>
                </>}
              </div>
              {results.map((m) => (
                <div key={m.merchant_id} className={`flex items-center gap-3 px-4 transition-colors hover:bg-slate-800/60 ${sel?.merchant_id === m.merchant_id ? "bg-blue-500/10" : ""}`}>
                <Checkbox data-testid={`select-merchant-${m.merchant_id}`} checked={checked.includes(m.merchant_id)}
                  onCheckedChange={(v) => setChecked((c) => (v ? [...c, m.merchant_id] : c.filter((x) => x !== m.merchant_id)))} />
                <button data-testid={`merchant-result-${m.merchant_id}`} onClick={() => open(m.merchant_id)}
                  className="flex-1 min-w-0 text-left py-3">
                  <div className="text-sm truncate">{m.merchant_name || "—"}<TagChips tags={tagMap[m.merchant_id]} /></div>
                  <div className="flex justify-between text-xs text-slate-500 font-mono mt-0.5"><span>{m.merchant_id}</span><span className="truncate ml-2">{m.owner}</span></div>
                </button>
                </div>
              ))}
            </div>
          )}
        </Panel>
        {!sel ? <div className="space-y-6"><PendingBrands /><PendingFees /><LostMerchants onOpen={open} /></div> : (
          <div className="space-y-6" data-testid="merchant-detail">
            <Panel>
              <div className="p-5 flex flex-wrap items-end justify-between gap-4">
                <div>
                  <div className="text-xl font-semibold">{sel.merchant_name || sel.merchant_id}</div>
                  <div className="text-xs font-mono text-slate-500 mt-1">{sel.merchant_id} · code {sel.agent_code}</div>
                  <div className="text-sm mt-2">Owner: <span className="text-blue-300" data-testid="merchant-owner">{sel.owner}</span></div>
                  <div className="mt-3"><TagEditor merchantId={sel.merchant_id} onChange={loadTags} /></div>
                </div>
                <div className="flex gap-8 font-mono">
                  <div><div className="text-[11px] uppercase text-slate-500">Total residual</div><div className="text-xl">{money(sel.total_residual)}</div></div>
                  <div><div className="text-[11px] uppercase text-slate-500">Total paid out</div><div className="text-xl text-emerald-400">{money(sel.total_payout)}</div></div>
                </div>
              </div>
            </Panel>
            <Panel title="Residual history"><div className="p-4"><TrendChart data={sel.history} keys={[{ key: "residual", label: "Residual", color: "#3B82F6" }, { key: "payout", label: "Payout", color: "#10B981" }]} /></div></Panel>
            <Panel>
              <table className="tbl w-full" data-testid="merchant-history-table">
                <thead><tr><th>Month</th><th>Owner</th><th className="!text-right">Volume</th><th className="!text-right">Residual</th><th className="!text-right">Fee</th><th className="!text-right">Split</th><th className="!text-right">Payout</th></tr></thead>
                <tbody>{[...sel.history].reverse().map((h) => (
                  <tr key={h.month}><td className="font-mono">{monthLabel(h.month)}</td><td className="text-slate-300">{h.owner}</td>
                    <td className="text-right font-mono text-slate-400">{money(h.volume)}</td><td className="text-right font-mono">{money(h.residual)}</td>
                    <td className="text-right font-mono text-slate-400" title={h.fee_rate ? `$${h.fee_rate}/mo rate` : ""}>{money(h.fee)}</td><td className="text-right font-mono">{pct(h.split_pct)}</td>
                    <td className="text-right font-mono text-emerald-400">{money(h.payout)}</td></tr>
                ))}</tbody>
              </table>
            </Panel>
            <NotesPanel targetType="merchant" targetId={sel.merchant_id} />
            <MerchantBrandPanel merchantId={sel.merchant_id} />
            <MerchantFeePanel merchant={sel} onDone={() => open(sel.merchant_id)} />
            <ReassignPanel merchant={sel} onDone={() => open(sel.merchant_id)} />
            <button data-testid="show-lost-merchants" onClick={() => setSel(null)} className="text-xs text-slate-500 hover:text-slate-200">← Back to lost merchants</button>
          </div>
        )}
      </div>
        </TabsContent>
      </Tabs>
      <BulkReassignDialog open={moveOpen} onClose={() => setMoveOpen(false)} merchantIds={checked} onDone={afterMove} />
    </div>
  );
}
